// ============================================================================
// BOOKING TAX BREAKDOWN — groups a booking's tax lines for display
// ============================================================================
// The API stores one tax line per priced line and tax component
// (BookingFull.taxLines). Summaries show them per component and rate
// ("CGST @ 2.5%"), split into room/meal charges and add-ons, and into tax
// added on top (exclusive) and tax already inside the rates (inclusive).
// The same grouping is used by the booking confirmation PDF
// (BookingConfirmationPdfService.TaxBreakdown).

import type { BookingTaxLine } from 'api/types/booking.types';

export interface TaxBreakdownRow {
  key: string;
  /** e.g. "CGST @ 2.5%" */
  label: string;
  amount: number;
}

export interface BookingTaxBreakdown {
  /** Room and meal charges — tax added on top. */
  charges: TaxBreakdownRow[];
  /** Room and meal charges — tax inside tax-inclusive rates. */
  chargesIncluded: TaxBreakdownRow[];
  /** Add-ons — tax added on top. */
  addons: TaxBreakdownRow[];
  /** Add-ons — tax inside tax-inclusive rates. */
  addonsIncluded: TaxBreakdownRow[];
  chargesTaxTotal: number;
  addonsTaxTotal: number;
  hasLines: boolean;
}

/** Invoice order: CGST, SGST, UTGST, IGST, then any other component (cess …) alphabetically. */
const COMPONENT_ORDER = ['CGST', 'SGST', 'UTGST', 'IGST'];

const componentRank = (code: string): number => {
  const i = COMPONENT_ORDER.indexOf(code.toUpperCase());
  return i >= 0 ? i : COMPONENT_ORDER.length;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export const formatTaxRate = (rate: number): string => {
  const r = Number(rate) || 0;
  return `${Number.isInteger(r) ? r.toFixed(0) : String(Number(r.toFixed(4)))}%`;
};

const group = (lines: BookingTaxLine[]): TaxBreakdownRow[] => {
  const rows = new Map<string, TaxBreakdownRow & { code: string; rate: number }>();
  lines.forEach((l) => {
    const code = (l.componentCode || '').toUpperCase();
    const rate = Number(l.ratePercentage) || 0;
    const key = `${code}|${rate}`;
    const row = rows.get(key);
    if (row) {
      row.amount = round2(row.amount + (Number(l.taxAmount) || 0));
    } else {
      rows.set(key, { key, code, rate, label: `${code} @ ${formatTaxRate(rate)}`, amount: round2(Number(l.taxAmount) || 0) });
    }
  });
  return Array.from(rows.values())
    .filter((r) => r.amount !== 0)
    .sort((a, b) => componentRank(a.code) - componentRank(b.code) || a.code.localeCompare(b.code) || a.rate - b.rate)
    .map(({ key, label, amount }) => ({ key, label, amount }));
};

const sum = (rows: TaxBreakdownRow[]) => round2(rows.reduce((s, r) => s + r.amount, 0));

export const buildBookingTaxBreakdown = (taxLines?: BookingTaxLine[] | null): BookingTaxBreakdown => {
  const lines = taxLines ?? [];
  const isAddon = (l: BookingTaxLine) => (l.chargeType || '').toUpperCase() === 'ADDON';

  const charges = group(lines.filter((l) => !isAddon(l) && !l.isTaxInclusive));
  const chargesIncluded = group(lines.filter((l) => !isAddon(l) && l.isTaxInclusive));
  const addons = group(lines.filter((l) => isAddon(l) && !l.isTaxInclusive));
  const addonsIncluded = group(lines.filter((l) => isAddon(l) && l.isTaxInclusive));

  return {
    charges,
    chargesIncluded,
    addons,
    addonsIncluded,
    chargesTaxTotal: sum(charges),
    addonsTaxTotal: sum(addons),
    hasLines: lines.length > 0,
  };
};
